import axios, { AxiosResponse, CancelToken } from "axios"
import { UUIDType } from "uuid"
import { InsertCuentaBancariaContactoCommandProps } from "./Commands/CuentaBancariaInsertCuentaBancariaContactoCommand"
import { PublishImportarContactosSageCommand } from "./Commands/PublishImportarContactosSageCommand"
import { UpdateCuentaBancariaContactoCommandProps } from "./Commands/UpdateCuentaBancariaContactoCommand"
import {
  contactoByFilterSimpleAdapter,
  contactoByIdAdapter,
  contactoCtaBancariasByEmpresaIdAdapter,
  contactoPreferenciasIdAdapter,
  getContactoByFiltersAdapter,
  getContactoContabilidadByIdAdapter,
  getContactoSaldoPendienteAdapter,
} from "./Contacto.adapter"
import { ContabilidadContactoDtoProps } from "./DTOs/ContabilidadContactoDto"
import { ContactoConfiguracionReadingDtoProps } from "./DTOs/ContactoConfiguracionReadingDto"
import { ContactoCreationDto } from "./DTOs/ContactoCreationDto"
import { ContactoReadingDtoProps } from "./DTOs/ContactoReadingDto"
import { ContactoReadingSimpleDto } from "./DTOs/ContactoReadingSimpleDto"
import { CuentaBancariaContactoReadingDtoProps } from "./DTOs/CuentaBancariaContactoReadingDto"
import { EmpresaGetUsuarioLoggedDtoProps } from "./DTOs/EmpresaGetUsuarioLoggedDto"
import { GetSaldoPendienteContactoDtoProps } from "./DTOs/GetSaldoPendienteContactoDto"
import { ContactoReadingDtoIPagedEnumerableProps } from "./Schemas/ContactoReadingDtoIPagedEnumerable"
import { ContactoReadingSimpleDtoIPagedEnumerableProps } from "./Schemas/ContactoReadingSimpleDtoIPagedEnumerable"
import { StringIPagedEnumerableProps } from "./Schemas/StringIPagedEnumerable"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export async function getContactoCtasBancariasByEmpresaId(
  empresaId: string
): Promise<AxiosResponse<Array<CuentaBancariaContactoReadingDtoProps>>> {
  const { data } = await axios.get(
    `/Contacto/empresa/${empresaId}/cuentas-bancarias`
  )
  return data.map((cuenta: CuentaBancariaContactoReadingDtoProps) =>
    contactoCtaBancariasByEmpresaIdAdapter(cuenta)
  )
}

export async function getContactoPreferenciasById(
  id: string
): Promise<ContactoConfiguracionReadingDtoProps> {
  const { data } = await axios.get(`/Contacto/${id}/preferencias`)
  return contactoPreferenciasIdAdapter(data)
}

export function deleteContactoCtasBancariasByEmpresaId(
  empresaContactoId: string,
  cuentaId: string
): Promise<void> {
  return axios.delete(
    `/Contacto/${empresaContactoId}/cuenta-bancaria/${cuentaId}`
  )
}

export function deleteContactoById(id: string): Promise<AxiosResponse<void>> {
  return axios.delete(`/Contacto/${id}`)
}

export interface ContactoByFiltersQueryParamsProps {
  Cif?: string
  Direccion?: string
  Email?: string
  EmpresaId?: string
  Etiquetas?: string[]
  IsExportEnabled?: boolean
  Keyword?: string
  RazonSocial?: string
  Telefono?: string
  SortingCriteria?: SortingCriteria
  MaxResultCount?: number
  SkipCount?: number
}

/**
 * Retrieves a list of contacts based on the provided filters.
 * @param queryParams - The query parameters for filtering the contacts.
 * @returns A promise that resolves to a paged enumerable of contact reading DTOs.
 */
// eslint-disable-next-line import/no-mutable-exports
export async function getContactoByFilters(
  queryParams: ContactoByFiltersQueryParamsProps,
  cancelToken?: CancelToken
): Promise<ContactoReadingDtoIPagedEnumerableProps> {
  const { data }: { data: ContactoReadingDtoIPagedEnumerableProps } =
    await axios.get("/Contacto/by-filters", {
      params: queryParams,
      cancelToken,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((contacto: ContactoReadingDtoProps) =>
      getContactoByFiltersAdapter(contacto)
    ),
    totalCount: data.totalCount,
  }
}

export async function getContactoSaldoPendiente(
  id: string
): Promise<GetSaldoPendienteContactoDtoProps> {
  const { data }: Record<"data", GetSaldoPendienteContactoDtoProps> =
    await axios.get(`/Contacto/${id}/saldo-pendiente`)
  return getContactoSaldoPendienteAdapter(data)
}

export interface ByFilterSimpleContactosQueryParamsProps {
  Cif?: string
  Direccion?: string
  Email?: string
  EmpresaId?: UUIDType | string
  Etiquetas?: string[]
  IsExportEnabled?: boolean
  Keyword?: string
  RazonSocial?: string
  Telefono?: string
  SortingCriteria?: {
    sort?: "ASC" | "DESC"
    property?: string
  }
  MaxResultCount?: number
  SkipCount?: number
}

export async function getContactosByFilterSimple({
  queryParams,
}: {
  queryParams?: ByFilterSimpleContactosQueryParamsProps
}): Promise<ContactoReadingSimpleDtoIPagedEnumerableProps> {
  const response = await axios.get("/Contacto/by-filters-simple", {
    params: queryParams,
  })

  const { data } = response

  return {
    currentPage: data.currentPage,
    items: data.items.map((contacto: ContactoReadingSimpleDto) =>
      contactoByFilterSimpleAdapter(contacto)
    ),
    totalCount: data.totalCount,
  }
}

export async function getContactosExportar() {
  const response = await axios.get("/Contacto/exportar-excel", {
    responseType: "blob",
  })

  return response
}

export interface ContactoEtiquetasQueryParamsProps {
  Keyword?: string
  MaxResultCount?: number
  SkipCount?: number
}
/**
 * Retrieves the etiquetas (labels) associated with a contacto (contact).
 * @param queryParams - The query parameters for retrieving the etiquetas.
 * @returns A Promise that resolves to an object containing the paginated list of etiquetas.
 */
export async function getContactoEtiquetas(
  queryParams?: ContactoEtiquetasQueryParamsProps
): Promise<StringIPagedEnumerableProps> {
  const { data }: { data: StringIPagedEnumerableProps } = await axios.get(
    "/Contacto/etiquetas",
    {
      params: queryParams,
    }
  )
  return {
    currentPage: data.currentPage,
    items: data.items,
    totalCount: data.totalCount,
  }
}

export async function bulkDeleteByContactoId(
  ids: Array<UUIDType | string>
): Promise<AxiosResponse> {
  const res = await axios.delete("/Contacto/bulk-delete-by-ids", { data: ids })
  return res
}

export async function getCtasBancariasContactoById(
  id: UUIDType | string
): Promise<CuentaBancariaContactoReadingDtoProps[]> {
  const { data } = await axios.get(`/Contacto/empresa/${id}/cuentas-bancarias`)
  return data
}

export async function putContactoCtasBancarias({
  id,
  idCuentaBancaria,
  requestBody,
}: {
  id: string
  idCuentaBancaria: string
  requestBody: UpdateCuentaBancariaContactoCommandProps
}) {
  const resp = axios.put(
    `/Contacto/${id}/cuenta-bancaria/${idCuentaBancaria}`,
    requestBody
  )
  return resp
}

export async function postContactoCtasBancarias({
  id,
  requestBody,
}: {
  id: string
  requestBody: InsertCuentaBancariaContactoCommandProps
}) {
  const resp = await axios.post(`/Contacto/${id}/cuenta-bancaria`, requestBody)
  return resp
}

export async function postContactoInsert(contacto: ContactoCreationDto) {
  const resp = await axios.post("/Contacto/insert", contacto)
  return resp
}

export async function getContactosById(
  id: string
): Promise<ContactoReadingDtoProps> {
  const { data } = await axios.get(`/Contacto/${id}`)

  return contactoByIdAdapter(data)
}

export async function getContactosByNifAndEmpresaId(
  contactoNif: EmpresaGetUsuarioLoggedDtoProps["cif"],
  empresaId: EmpresaGetUsuarioLoggedDtoProps["id"]
): Promise<ContactoReadingDtoProps> {
  const { data } = await axios.get(
    `Contacto/nif/${contactoNif}/empresa/${empresaId}`
  )

  return contactoByIdAdapter(data)
}

export async function postContactosImportSage(
  documento: PublishImportarContactosSageCommand
) {
  return axios.post("/Contacto/importar-sage", documento, {
    headers: {
      "Content-Type": "application/json",
    },
  })
}

export async function getContactoContabilidadById(
  id: string
): Promise<ContabilidadContactoDtoProps> {
  const { data } = await axios.get(`/Contacto/${id}/contabilidad`)
  return getContactoContabilidadByIdAdapter(data)
}
